import { ToastModule } from 'primeng/toast';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, OnDestroy, signal } from '@angular/core';
import { commonService } from '@bl-src/app/core/services/common.service';
import { PlansService } from '@bl-src/app/core/services/plans.service';
import { SharedService } from '@s-app/common/services/shared.service';
import { Branches, NonSubscribedBranchesNoPaginate, PackageFeature, PackagePlan } from '@s-app/plans/plans';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccordionModule } from 'primeng/accordion';
import { SubscriptionPalnLoadingComponent } from './subscription-paln-loading/subscription-paln-loading.component';
import { PaymentMethodDialogComponent, PaymentMethodDialogData, PaymentMethodDialogCloseData } from './subscribe-page/payment-method-dialog/payment-method-dialog.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-subscripion-plans',
  standalone: true,
  imports: [CommonModule, TooltipModule, TranslateModule, AccordionModule, SubscriptionPalnLoadingComponent,ToastModule],
  providers: [DialogService, DynamicDialogRef,MessageService],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './subscripion-plans.component.html',
  styleUrl: './subscripion-plans.component.scss'
})
export class SubscripionPlansComponent implements OnDestroy {
  selectedPlan = signal<'cycle30' | 'cycle360'>('cycle360'); // Currently selected billing cycle
  plans = signal<PackageFeature[]>([]); // Plans loaded from API
  isLoading = signal<boolean>(false); // Loading indicator for plans fetch
  ref: DynamicDialogRef | undefined; // Dialog reference for payment method
  currentSlide = signal<number>(0); // Carousel active slide index
  itemsPerView = signal<number>(3); // Number of visible plan cards (responsive)
  touchStartX = signal<number>(0); // Touch start X coordinate
  touchEndX = signal<number>(0); // Touch end X coordinate
  private resizeListener: () => void; // Window resize listener for responsive carousel

  questions: { question: string, answer: string }[] = [ // FAQ questions displayed in accordion
    { question: 'ask_questions.question_1', answer: 'ask_questions.answer_1' },
    { question: 'ask_questions.question_2', answer: 'ask_questions.answer_2' },
    { question: 'ask_questions.question_3', answer: 'ask_questions.answer_3' },
    { question: 'ask_questions.question_4', answer: 'ask_questions.answer_4' },
  ]
  nonSubscribedBranches = signal<Branches[]>([]); // Branches eligible for new subscription

  constructor(
    public plansService: PlansService,
    public commonService: commonService,
    public router: Router,
    public shared: SharedService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {

  }

  /**
   * Initialize plans, responsive listeners, and language change handling
   */
  ngOnInit(): void {
    this.handleGetPlans();
    this.resizeListener = () => {
      this.updateItemsPerView();
      const maxSlide = this.getMaxSlide();
      if (this.currentSlide() > maxSlide) {
        this.currentSlide.set(maxSlide);
      }
    };
    window.addEventListener('resize', this.resizeListener);
    
    this.translate.onLangChange.subscribe(() => {
      this.resetCarouselForLanguageChange();
    });
  }

  /**
   * Fetch plans and initialize responsive layout
   */
  handleGetPlans(): void {
    this.isLoading.set(true);
    this.plansService.getPlans().subscribe((res: PackagePlan) => {
      this.plans.set(res.packageFeatures);
      this.updateItemsPerView();
      this.getNonSubscribedBranches()
      setTimeout(() => { this.isLoading.set(false); }, 1000);
    },
      (error) => {
        console.error(error);
      }
    );
  }

  /**
   * Fetch branches that are not currently subscribed
   */
  getNonSubscribedBranches(): void {
    this.plansService.getNonSubscribedBranches(false).subscribe((data: NonSubscribedBranchesNoPaginate) => {
      console.log(data.branches);

      this.nonSubscribedBranches.set(data.branches);
      console.log('Non-subscribed Branches:', data);
    },
      error => {
        console.error('Error fetching non-subscribed branches:', error);
      }
    );
  }

  /**
   * Open payment method selection dialog for the chosen plan
   */
  openPaymentMethodDialog(plan: PackageFeature): void {
    const header = localStorage.getItem('lang') === 'ar' ? 'اختر طريقة الدفع' : 'Select payment method';
    const title = localStorage.getItem('lang') === 'ar' ? 'الاشتراك في خطة' : 'Subscribe plan';
    this.ref = this.dialogService.open(PaymentMethodDialogComponent, {
      header: header,
      width: '35vw',
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
    },
      data: {
        branches: this.nonSubscribedBranches(),
        title: `${title} (${plan.name} - ${this.translate.instant(this.plansService.getPlanType(this.selectedPlan()))})`,
        price: plan[this.selectedPlan()].price,
        currency: plan[this.selectedPlan()].currency,
        period: this.plansService.getPlanType(this.selectedPlan())
      } as PaymentMethodDialogData,
      contentStyle: { overflow: 'auto' },
      closeOnEscape: false,
      closable: false,
      styleClass: 'custom-dialog-style',
    });
    this.ref.onClose.subscribe((data: PaymentMethodDialogCloseData) => {
      if (data&& data.brancheID) {
        this.shared.isLoading = true;
        const subscriptionData = {
          branchId: data.brancheID,
          subscriptionPlanPriceId: plan[this.selectedPlan()].planPriceId,
          cycle: this.getCycleNumber(),
          renewalOptions: 1,
          paymentMethodEnum: data.PaymentMethodEnum,
        };

        this.handleBuyPlan(subscriptionData)
      }
    });
  }

  /**
   * Execute subscription purchase and redirect to provider if URL returned
   */
  private handleBuyPlan(subscriptionData:{branchId:number,subscriptionPlanPriceId:number,cycle:number,renewalOptions:number,paymentMethodEnum:number}): void {

    this.plansService.subscribeToPlan(subscriptionData).subscribe(
      (response) => {
        console.log('Payment successful:', response);
        if (response.paymentProviderURL) {
          window.location.href = response.paymentProviderURL;
          this.shared.isLoading = false;
        }
      },
      (error) => {
        this.shared.isLoading = false;
      }
    );
  }

  /**
   * Compute per-month price equivalent depending on selected cycle
   */
  getPriceBasedOnPlan(plan: PackageFeature, selectedPlan: 'cycle30' | 'cycle360'): number {
    if (selectedPlan === 'cycle30') return plan[selectedPlan].price;
    else if (selectedPlan === 'cycle360') return plan[selectedPlan].price / 12;
    else  return 0;
  }



  /**
   * Map selected cycle to numeric duration value
   */
  getCycleNumber(): number {
    switch (this.selectedPlan()) {
      case 'cycle30':
        return CycleDuration.Cycle30;
      case 'cycle360':
        return CycleDuration.Cycle360;
      default:
        return CycleDuration.Cycle30;
    }
  }

  /**
   * Move to next carousel slide if available
   */
  nextSlide(): void {
    const maxSlide = this.getMaxSlide();
    if (this.currentSlide() < maxSlide) {
      this.currentSlide.set(this.currentSlide() + 1);
    }
  }

  /**
   * Move to previous carousel slide if available
   */
  previousSlide(): void {
    if (this.currentSlide() > 0) {
      this.currentSlide.set(this.currentSlide() - 1);
    }
  }

  /**
   * Jump to a specific carousel slide index
   */
  goToSlide(index: number): void {
    this.currentSlide.set(index);
  }

  /**
   * Build pagination dots based on itemsPerView and plans length
   */
  getDotsArray(): number[] {
    const maxSlides = Math.max(1, this.plans().length - (this.itemsPerView() - 1));
    return Array.from({ length: maxSlides }, (_, i) => i);
  }

  /**
   * Calculate the maximum valid slide index
   */
  getMaxSlide(): number {
    return Math.max(0, this.plans().length - this.itemsPerView());
  }

  /**
   * Percentage shift per slide for transform calculation
   */
  getTransformPercentage(): number {
    return 100 / this.itemsPerView();
  }

  /**
   * Build CSS translateX value considering RTL/LTR direction
   */
  getTransformValue(): string {
    const isRTL = this.commonService.getLang() === 'ar';
    const percentage = this.currentSlide() * this.getTransformPercentage();
    
    if (isRTL) return `translateX(${percentage}%)`;
    else return `translateX(-${percentage}%)`;
  }

  /**
   * Reset carousel when language changes to avoid position mismatch
   */
  resetCarouselForLanguageChange(): void {
    this.currentSlide.set(0);
  }

  /**
   * Capture touch start position for swipe handling
   */
  onTouchStart(event: TouchEvent): void {
    this.touchStartX.set(event.touches[0].clientX);
  }

  /**
   * Capture touch end position and perform swipe action
   */
  onTouchEnd(event: TouchEvent): void {
    this.touchEndX.set(event.changedTouches[0].clientX);
    this.handleSwipe();
  }

  /**
   * Determine swipe direction and advance carousel accordingly
   */
  private handleSwipe(): void {
    const swipeThreshold = 50;
    const diff = this.touchStartX() - this.touchEndX();
    const isRTL = this.commonService.getLang() === 'ar';

    if (Math.abs(diff) > swipeThreshold) {
      if (isRTL) {
        if (diff < 0) this.nextSlide();
        else this.previousSlide();
      } else {
        if (diff > 0) this.nextSlide();
        else this.previousSlide();
      }
    }
  }

  /**
   * Update itemsPerView responsively based on viewport width
   */
  private updateItemsPerView(): void {
    const width = window.innerWidth;
    if (width <= 767) this.itemsPerView.set(1);
    else if (width <= 1125) this.itemsPerView.set(2);
    else this.itemsPerView.set(3);
  }

  /**
   * Navigate to contact us page
   */
  navigateToContactUs(): void {
    this.router.navigate(['/contact-us']);
  }

  /**
   * Clean up global listeners on destroy
   */
  ngOnDestroy(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
}
enum CycleDuration {
  Cycle30 = 30,
  Cycle360 = 360
}
